import { useEffect, useState } from "react";
import { KeyRound, Loader2, Trash2, UserPlus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../ui/switch";
import { school, formatApiError } from "../../lib/api";
import { CredentialsCard } from "./CredentialsCard";
import { SubscriptionCard } from "./SubscriptionCard";
import { ProgressCard } from "./ProgressCard";
import { AgePicker } from "../AgePicker";

const StudentRow = ({ s, onReset, onRemove, onAge }) => (
  <tr data-testid="student-row" className="border-t border-white/[0.06]">
    <td className="py-3 text-slate-200">{s.name}</td>
    <td data-testid="student-username" className="font-mono text-sm text-slate-400">{s.username}</td>
    <td className="py-2"><AgePicker compact value={s.kid_age} onChange={(a) => onAge(s, a)} testid={`student-age-${s.username}`} /></td>
    <td className="text-right whitespace-nowrap">
      <button data-testid="reset-student-password-btn" onClick={() => onReset(s)} className="text-slate-500 hover:text-white mr-4" title="Reset password"><KeyRound size={15} /></button>
      <button data-testid="remove-student-btn" onClick={() => onRemove(s)} className="text-slate-500 hover:text-red-400" title="Remove student"><Trash2 size={15} /></button>
    </td>
  </tr>
);

export const ClassDetail = ({ classId, info, onChanged }) => {
  const [cls, setCls] = useState(null);
  const [names, setNames] = useState("");
  const [creds, setCreds] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => school.get(classId).then(setCls);
  useEffect(() => { load().catch(() => toast.error("Couldn't load the class")); }, [classId]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn) => {
    try {
      await fn();
      await load();
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const add = async (e) => {
    e.preventDefault();
    const list = names.split("\n").map((n) => n.trim()).filter(Boolean);
    if (!list.length) return;
    setBusy(true);
    await act(async () => {
      const { created } = await school.addStudents(classId, list);
      setCreds(created);
      setNames("");
    });
    setBusy(false);
  };

  const reset = (s) => act(async () => setCreds([await school.resetStudent(classId, s.user_id)]));
  const remove = (s) => act(async () => {
    await school.removeStudent(classId, s.user_id);
    toast.success(`${s.name} removed`);
  });
  const toggleKids = (v) => act(() => school.update(classId, { kids_mode: v }));

  if (!cls) return <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-slate-500" /></div>;
  const left = cls.seats - cls.student_count;
  const home = cls.kind === "homeschool";

  return (
    <section data-testid="class-detail" className="space-y-6 min-w-0">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 data-testid="class-detail-name" className="font-display text-2xl font-bold text-white">{cls.name}</h2>
          <p data-testid="class-seat-count" className="text-sm font-mono text-slate-500 mt-1">{cls.student_count} of {cls.seats} {home ? "children" : "students"}</p>
        </div>
        <label className="flex items-center gap-3 text-sm text-slate-300">
          <ShieldCheck size={15} className="text-[#38BDF8]" /> Kids Mode for {home ? "children" : "students"}
          <Switch data-testid="class-kids-mode-switch" checked={cls.kids_mode} onCheckedChange={toggleKids} />
        </label>
      </div>
      <SubscriptionCard cls={cls} info={info} onChanged={() => act(async () => {})} />
      {home && <ProgressCard key={cls.student_count} classId={classId} />}
      {creds && <CredentialsCard creds={creds} home={home} onClose={() => setCreds(null)} />}
      <form onSubmit={add} className="what-plan space-y-3">
        <p className="font-display font-semibold text-white flex items-center gap-2"><UserPlus size={16} /> {home ? "Add children" : "Add students"}</p>
        <p className="text-xs text-slate-500">One name per line. We'll create a username and simple password for each {home ? "child" : "student"} ({left} seats left).</p>
        <textarea data-testid="add-students-textarea" value={names} onChange={(e) => setNames(e.target.value)} rows={4} placeholder={"Thandi Mokoena\nLiam Smith"} className="what-input resize-y" />
        <button data-testid="add-students-btn" disabled={busy || !names.trim() || left <= 0} className="what-btn-primary">
          {busy && <Loader2 size={15} className="animate-spin" />} {home ? "Create logins" : "Create student logins"}
        </button>
      </form>
      <div className="what-plan overflow-x-auto">
        <p className="font-display font-semibold text-white mb-2">{home ? "Children" : "Students"}</p>
        {cls.students.length === 0 ? (
          <p data-testid="no-students" className="text-sm text-slate-500">{home ? "No children yet." : "No students yet."}</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs font-mono uppercase tracking-wider text-slate-500"><th className="py-2">Name</th><th>Username</th><th>Age</th><th /></tr></thead>
            <tbody>{cls.students.map((s) => <StudentRow key={s.user_id} s={s} onReset={reset} onRemove={remove} onAge={(st, a) => act(() => school.setAge(classId, st.user_id, a))} />)}</tbody>
          </table>
        )}
        <p className="text-xs text-slate-500 mt-3">Students log in at the WHAT? login page with their username and password, and a grown-up types the sign-in code (teacher code, or the parent's Kids Mode PIN for homeschool).</p>
      </div>
    </section>
  );
};
